import { isClientIntakePrivateDocument } from "./lib/client-intake-bootstrap";
import { isClientIntakeUrl } from "@shared/client-intake-privacy";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import App from "./App";
import "./index.css";
import { installModulePreloadRecovery, reloadAfterStaleModuleFailure } from "./lib/module-load-recovery";

// During a dev-server restart, an already-open Preview iframe can request a
// now-stale lazy-module URL. Vite reports that as `vite:preloadError`; without
// a full reload React can remain mounted with mismatched modules and leave the
// iframe blank. Retry once per short window so the Preview recovers as soon as
// the server is back, without a reload loop if it is still booting.
installModulePreloadRecovery();

function isStaleDynamicModuleFailure(reason: unknown) {
  const message = reason instanceof Error ? reason.message : String(reason);
  return /failed to fetch dynamically imported module|error loading dynamically imported module/i.test(message);
}


// Initialize Sentry BEFORE React renders so early crashes are captured. No-ops
// cleanly when VITE_SENTRY_DSN is not set. The DSN must be VITE_-prefixed to be
// inlined into the client bundle.
const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
if (dsn && !isClientIntakeUrl(window.location.href)) {
  Sentry.init({
    dsn,
    // Only send events in production builds, even when a DSN is configured in
    // dev. import.meta.env.PROD is Vite's canonical equivalent of
    // process.env.NODE_ENV === "production" for browser code.
    enabled: import.meta.env.PROD,
    environment: import.meta.env.MODE,
    // Tag browser events with the build's resolved release so they (and the
    // uploaded client source maps) resolve against the same Sentry release as
    // the backend and the postbuild release commands. Vite's `define` bakes
    // SENTRY_RELEASE (and the REPLIT_GIT_COMMIT_SHA fallback) into the bundle at
    // build/transform time; the typeof guard keeps the expression safe in the
    // browser if a replacement hasn't fired (process is undefined there).
    release:
      (typeof process !== "undefined"
        ? process?.env?.SENTRY_RELEASE || process?.env?.REPLIT_GIT_COMMIT_SHA
        : undefined) || undefined,
    // Sentry Logs: forward browser console output into the Sentry Logs view.
    // Sending is still gated by `enabled` (production-only) above, so dev runs
    // and DSN-unset runs stay quiet.
    enableLogs: true,
    integrations: [
      Sentry.browserTracingIntegration({ shouldCreateSpanForRequest: url => !isClientIntakePrivateDocument() && !isClientIntakeUrl(url) }),
      Sentry.replayIntegration({ beforeAddRecordingEvent: event => isClientIntakePrivateDocument() ? null : event }),
      Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
    ],
    tracesSampleRate: 0.2,
    beforeSend: event => isClientIntakePrivateDocument() || isClientIntakeUrl(event.request?.url) ? null : event,
    beforeSendTransaction: event => isClientIntakePrivateDocument() || isClientIntakeUrl(event.request?.url) ? null : event,
    beforeBreadcrumb: breadcrumb => isClientIntakePrivateDocument() ||
      [breadcrumb.data?.url, breadcrumb.data?.from, breadcrumb.data?.to].some(isClientIntakeUrl) ? null : breadcrumb,
    beforeSendLog: log => isClientIntakePrivateDocument() ? null : log,
    // Session Replay: don't record normal sessions, but capture 100% of
    // sessions in which an error occurs.
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
    // Suppress common, non-actionable browser noise.
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "Non-Error promise rejection captured",
    ],
    // Drop events originating from browser extensions / injected third-party
    // scripts that we don't control.
    denyUrls: [/extensions\//i, /^chrome:\/\//i],
  });
}

// Global capture for uncaught errors / rejections outside React's tree. Sentry's
// default global handlers also catch these; its dedupe integration drops the
// duplicate, so these explicit handlers are safe and make intent clear.
window.addEventListener("error", (event) => {
  if (isStaleDynamicModuleFailure(event.error ?? event.message)) {
    reloadAfterStaleModuleFailure(event.error ?? event.message);
  }
  Sentry.captureException(event.error ?? event.message);
});
window.addEventListener("unhandledrejection", (event) => {
  if (isStaleDynamicModuleFailure(event.reason)) {
    reloadAfterStaleModuleFailure(event.reason);
  }
  Sentry.captureException(event.reason);
});

createRoot(document.getElementById("root")!).render(
  <Sentry.ErrorBoundary
    fallback={
      <div style={{ padding: 24, fontFamily: "Inter, sans-serif" }}>
        Something went wrong. Please refresh the page.
      </div>
    }
  >
    <App />
  </Sentry.ErrorBoundary>,
);
